import { TimeScale, TimeSeriesDataset, TimeSeriesDatasetSource } from '../../core';
import { ChartApi } from '../EChart';
import { DatapointInfo } from '../model';

export interface ZoomEventData {
  start: number;
  end: number;
  // TODO: remove startIndex and endIndex once LineChart is deprecated
  startIndex?: number;
  endIndex?: number;
}

/**
 * Enable dataZoom without requring user to click toolbox icon.
 * https://stackoverflow.com/questions/57183297/is-there-a-way-to-use-zoom-of-type-select-without-showing-the-toolbar
 */
export function enableDataZoom(chartApi: ChartApi) {
  const chartModel = chartApi.getChartModel();
  if (!chartModel) return;
  if (chartModel.option.toolbox !== undefined && chartModel.option.toolbox.length > 0) {
    // check if hidden data zoom icon is unselected (if selected it would be 'emphasis' instead of 'normal')
    if (chartModel.option.toolbox[0].feature.dataZoom.iconStatus.zoom === 'normal') {
      chartApi.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      });
    }
  }
}

/**
 * Restore chart to original state before zoom or other actions were dispatched
 */
export function restoreChart(chartApi: ChartApi) {
  // TODO: support incremental unzoom instead of restore to original state
  chartApi.dispatchAction({
    type: 'restore', // https://echarts.apache.org/en/api.html#events.restore
  });
}

/*
 * Clear all highlighted series when cursor exits canvas
 * https://echarts.apache.org/en/api.html#action.downplay
 */
export function clearHighlightedSeries(chartApi: ChartApi) {
  if (chartApi.dispatchAction !== undefined) {
    // Clear any selected data points
    chartApi.dispatchAction({
      type: 'unselect',
    });

    // Clear any highlighted series
    chartApi.dispatchAction({
      type: 'downplay',
    });
  }
}

/*
 * Convert a point from pixel coordinate to logical coordinate.
 * Used to determine if cursor is over chart canvas and closest datapoint.
 * https://echarts.apache.org/en/api.html#echartsInstance.convertFromPixel
 */
export function getPointInGrid(cursorCoordX: number, cursorCoordY: number, chartApi?: ChartApi) {
  if (chartApi === undefined) {
    return null;
  }

  const pointInPixel = [cursorCoordX, cursorCoordY];

  return chartApi.convertFromPixel('grid', pointInPixel);
}

/*
 * TimeSeriesChart tooltip is built custom to support finding nearby series instead of single or all series.
 * This means ECharts actions need to be dispatched manually for series highlighting, datapoint select state, etc.
 * More info: https://echarts.apache.org/en/api.html#action
 */
export function batchDispatchNearbySeriesActions(
  chartApi: ChartApi,
  emphasizedSeriesIndexes: number[],
  nonEmphasizedSeriesIndexes: number[],
  emphasizedDatapoints: DatapointInfo[],
  duplicateDatapoints: DatapointInfo[]
) {
  // Accounts for multiple series that are rendered direct on top of eachother.
  // Only applies select state to the datapoint that is visible to avoid color mismatch.
  const lastEmphasizedDatapoint =
    duplicateDatapoints.length > 0
      ? duplicateDatapoints[duplicateDatapoints.length - 1]
      : emphasizedDatapoints[emphasizedDatapoints.length - 1];
  if (lastEmphasizedDatapoint !== undefined && duplicateDatapoints.length > 0) {
    // Used to increase datapoint symbol size, corresponds to select options inside getTimeSeries util.
    // https://echarts.apache.org/en/option.html#series-line.select.itemStyle
    chartApi.dispatchAction({
      type: 'select',
      seriesIndex: lastEmphasizedDatapoint.seriesIndex,
      dataIndex: lastEmphasizedDatapoint.dataIndex,
      // Shared crosshair should not emphasize datapoints on adjacent charts.
      escapeConnect: true,
    });
  } else {
    // Clear previously selected datapoints.
    chartApi.dispatchAction({
      type: 'toggleSelect',
    });
    // If there are not datapoints directly on top of eachother, ensure all nearby datapoints are selected (aka shown larger)
    for (const datapoint of emphasizedDatapoints) {
      const { seriesIndex, dataIndex } = datapoint;
      chartApi.dispatchAction({
        type: 'select',
        seriesIndex,
        dataIndex,
        // Shared crosshair should not emphasize datapoints on adjacent charts.
        escapeConnect: true, // TODO: try to remove escapeConnect and match by seriesName for cross panel correlation
      });
    }
  }

  // Clears emphasis state of all lines that are not emphasized.
  // Emphasized is a subset of just the nearby series that are closest to cursor.
  chartApi.dispatchAction({
    type: 'downplay',
    seriesIndex: nonEmphasizedSeriesIndexes,
  });

  // https://echarts.apache.org/en/api.html#action.highlight
  if (emphasizedSeriesIndexes.length > 0) {
    // Highlight closest series and fadeout opacity of all series far away from cursor.
    highlightSeries(chartApi, emphasizedSeriesIndexes);
  } else {
    // Clears selected datapoints since no bold series in tooltip, restore does not impact highlighting
    chartApi.dispatchAction({
      type: 'toggleSelect', // https://echarts.apache.org/en/api.html#action.toggleSelect
    });
  }
}

/*
 * Find closest timestamp to logical x coordinate returned from echartsInstance.convertFromPixel
 * Used to find nearby series in time series tooltip.
 */
export function getClosestTimestamp(
  timeSeriesValues: TimeSeriesDatasetSource[] | undefined,
  cursorX: number,
  timeScale?: TimeScale
) {
  if (!timeSeriesValues) {
    return { closestTimestamp: null, closestTimestampIndex: null };
  }

  // Calculate an estimated index for the closest timestamp to optimize best case lookup.
  // 1. Subtract startMs from cursorX to normalize cursorX to the same scale as the timestamps.
  // 2. Divide the result by stepMs to convert the time difference to an array index (assumes a uniform distribution of timestamps).
  // 3. Round the result to the nearest integer to obtain a valid array index. If the index is invalid, fallback to linear search.
  if (timeScale !== undefined && typeof timeScale.stepMs === 'number' && timeScale.stepMs > 0) {
    const estimatedIndex = Math.round((cursorX - timeScale.startMs) / timeScale.stepMs);
    const estimatedClosestTimeSeries = timeSeriesValues[estimatedIndex];
    if (estimatedClosestTimeSeries) {
      const [estimatedClosestTimestamp] = estimatedClosestTimeSeries;
      return { closestTimestamp: estimatedClosestTimestamp, closestTimestampIndex: estimatedIndex };
    }
  }

  // Fallback to linear search if timeScale is not provided
  let currentClosestTimestamp: number | null = null;
  let currentClosestIndex: number | null = null;
  let currentClosestDistance = Infinity;
  for (const [index, [timestamp]] of timeSeriesValues.entries()) {
    const distance = Math.abs(timestamp - cursorX);
    if (distance < currentClosestDistance) {
      currentClosestTimestamp = timestamp;
      currentClosestIndex = index;
      currentClosestDistance = distance;
    }
  }

  return { closestTimestamp: currentClosestTimestamp, closestTimestampIndex: currentClosestIndex };
}

/**
 * Wrapper version of getClosestTimestamp that includes pointInGrid conversion
 */
export function checkDatasetForClosestTimestamp(
  data: TimeSeriesDataset[],
  pointInGrid: number[],
  timeScale?: TimeScale
) {
  const cursorX: number | null = pointInGrid[0] ?? null;

  if (cursorX === null) {
    return { closestTimestamp: null, closestTimestampIndex: null };
  }

  // Only need to loop through first dataset source since getCommonTimeScale ensures xAxis timestamps are consistent
  const firstTimeSeriesValues = data[0]?.source ?? [];
  return getClosestTimestamp(firstTimeSeriesValues, cursorX, timeScale);
}

/*
 * Highlight a single series with correct dispatchAction properties
 * https://echarts.apache.org/en/api.html#action.highlight
 */
export function highlightSeries(chartApi: ChartApi, seriesIndex: number | number[]) {
  if (chartApi.dispatchAction !== undefined) {
    chartApi.dispatchAction({
      type: 'highlight',
      seriesIndex,
      notBlur: false, // false ensures blur IS triggered, so non-focused series fade out
      escapeConnect: true, // shared crosshair should not emphasize series on adjacent charts
    });
  }
}
